import type { PapiAttachmentFile, PapiDeviation } from '@wix/da-papi-types';

export type UploadState = {
  fileUploads: FileUploadInfo[];
  fileUploadsWaiting: FileUploadInfo[];
  fileUploadsCompleted: FileUploadInfo[];
  attachmentUploads: AttachmentUploadInfo[];
  attachmentUploadsWaiting: AttachmentUploadInfo[];
  attachmentUploadsCompleted: AttachmentUploadInfo[];
  attachmentDeviationLocked: Record<number, boolean>; // Some deviations like repositions and removals need to be atomic, and we need to lock the deviation
  documentUploads: {
    narf: DocumentUploadInfo | undefined;
    sprf: DocumentUploadInfo | undefined;
  };
};

export type UploadFilePayload = {
  file: File;
  uploadId: number;
  deviationId?: number;
  folderId?: number;
  /** stash protect only */
  folderName?: string;
  tag?: string;
  isDraft?: boolean;
  isPreviewUpload?: boolean;
  useDefaults?: boolean;
  onSuccess?: UploadSuccessCallback;
  onFailure?: UploadFailureCallback;
};

export interface UploadInfo {
  error?: UploadError;
  progress?: number;
}

export interface FileUploadInfo extends UploadFilePayload, UploadInfo {}

export type UploadSuccessCallback = (payload: {
  uploadId: number;
  deviationId: number;
  deviation?: PapiDeviation;
  file: File;
}) => void;

export type UploadFailureCallback = (
  payload: { uploadId: number },
  error: UploadError
) => void;

// TODO: Reintroduce when backend lands
// export type AttachmentType = PapiRequestDaSharedDeviationAttachmentsAdd['type'];
export type AttachmentType = 'pcp' | 'exclusive' | 'additional_image';

export type UploadAttachmentPayload = {
  tag?: string;
  file: File;
  uploadId: number;
  parentDeviationId: number;
  type: AttachmentType;
  onSuccess?: UploadAttachmentSuccessCallback;
  onFailure?: UploadFailureCallback;
};

export interface AttachmentUploadInfo
  extends UploadAttachmentPayload,
    UploadInfo {}

export type UploadAttachmentSuccessCallback = (payload: {
  uploadId: number;
  fileId: number;
  deviationId: number;
  file: File;
  deviation?: PapiDeviation;
  attachment?: PapiAttachmentFile;
}) => void;

export type LegalDocumentType = 'narf' | 'sprf';

export type UploadDocumentPayload = {
  file: File;
  deviationId: number;
  documentType: LegalDocumentType;
  onSuccess: (documentUrl: string) => void;
};

export interface DocumentUploadInfo extends UploadDocumentPayload {
  error?: UploadError;
  progress?: number;
  documentUrl?: string;
}

export enum UploadErrorCode {
  ITEM_COUNT_QUOTA_LIMIT_REACHED = 2,
}

export interface ServerError extends Error {
  isServerError: true;
}

export type UploadError = Error | ServerError;

export type UploadType = 'deviation' | 'document' | 'attachment';

// Keep in sync with lib.files.deviations in the backend
export enum AttachmentUsageType {
  USAGETYPE_PURCHASABLE_STASH_ZIP = 12,
  USAGETYPE_ORIGINAL_FILE = 18,
  USAGETYPE_ORIGINAL_CUSTOM_FULLVIEW_IMAGE = 19,
  USAGETYPE_WIX_VIDEO_360P = 20,
  USAGETYPE_WIX_VIDEO_480P = 21,
  USAGETYPE_WIX_VIDEO_720P = 22,
  USAGETYPE_WIX_VIDEO_1080P = 23,
  USAGETYPE_MURO_INPAINT_BASE = 24,
  USAGETYPE_MURO_INPAINT_MASK = 25,
  USAGETYPE_MURO_INPAINT_RESULT = 26,
  USAGETYPE_FREE_DOWNLOAD_FILE = 27,
  USAGETYPE_FREE_DOWNLOAD_ZIP = 28,
  USAGETYPE_PCP_ASSET = 29,
  USAGETYPE_PCP_ZIP = 30,
  USAGETYPE_ADDITIONAL_IMAGE = 31,
  USAGETYPE_MURO_LAYER_IMAGE = 32,
}
